import { ArrowUpRight, Play, Youtube } from "lucide-react";
import Reveal from "./Reveal";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { BEFORE_AFTER, BRAND, VIDEOS } from "@/config/clinicConfig";

export default function BeforeAfter() {
  return (
    <section id="results" data-testid="before-after-section" className="pt-10 pb-24 lg:pt-14 lg:pb-32">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <Reveal className="text-center">
          <h2 className="font-display text-3xl font-medium tracking-tight text-espresso sm:text-4xl lg:text-[2.75rem]">
            {BEFORE_AFTER.heading}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-cocoa">{BEFORE_AFTER.sub}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <Carousel data-testid="before-after-carousel" className="mt-14" opts={{ align: "start", loop: true }}>
            <CarouselContent>
              {BEFORE_AFTER.cases.map((c) => (
                <CarouselItem key={c.id} className="basis-full sm:basis-1/2 lg:basis-1/3">
                  <div
                    data-testid={`ba-slide-${c.id}`}
                    className="flex h-full flex-col overflow-hidden rounded-3xl border border-espresso/10 bg-espresso shadow-[0_24px_60px_-30px_rgba(44,34,30,0.5)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5"
                  >
                    <div className="flex aspect-square items-center justify-center p-2">
                      <img
                        src={c.image}
                        alt={c.alt}
                        loading="lazy"
                        className="h-full w-full rounded-2xl object-contain"
                      />
                    </div>
                    <div className="bg-cream px-5 py-4">
                      <p className="font-display text-base font-semibold leading-snug tracking-tight text-espresso">
                        {c.title}
                      </p>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-clay">
                        {c.sessions}
                      </p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious
              data-testid="before-after-prev-button"
              className="-left-3 h-10 w-10 rounded-full border-espresso/15 bg-cream text-espresso hover:bg-clay hover:text-cream lg:-left-5"
            />
            <CarouselNext
              data-testid="before-after-next-button"
              className="-right-3 h-10 w-10 rounded-full border-espresso/15 bg-cream text-espresso hover:bg-clay hover:text-cream lg:-right-5"
            />
          </Carousel>
        </Reveal>

        <Reveal delay={0.2} className="mt-20">
          <h2 className="text-center font-display text-3xl font-medium tracking-tight text-espresso sm:text-4xl">
            {VIDEOS.heading}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-base text-cocoa">{VIDEOS.sub}</p>
          <Carousel data-testid="video-carousel" className="mt-10" opts={{ align: "start", loop: false }}>
            <CarouselContent>
              {VIDEOS.items.map((v) => (
                <CarouselItem key={v.id} className="basis-full sm:basis-1/2">
                  <div
                    data-testid={`video-slide-${v.id}`}
                    className="overflow-hidden rounded-3xl border border-espresso/10 bg-espresso shadow-[0_24px_60px_-30px_rgba(44,34,30,0.5)]"
                  >
                    <iframe
                      src={`${v.embed}?rel=0`}
                      title={v.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      loading="lazy"
                      className="aspect-video w-full"
                    ></iframe>
                    <p className="bg-cream px-5 py-4 font-display text-base font-semibold tracking-tight text-espresso">
                      {v.title}
                    </p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious
              data-testid="video-prev-button"
              className="-left-3 h-10 w-10 rounded-full border-espresso/15 bg-cream text-espresso hover:bg-clay hover:text-cream lg:-left-5"
            />
            <CarouselNext
              data-testid="video-next-button"
              className="-right-3 h-10 w-10 rounded-full border-espresso/15 bg-cream text-espresso hover:bg-clay hover:text-cream lg:-right-5"
            />
          </Carousel>
        </Reveal>

        <Reveal delay={0.15} className="mt-8 text-center">
          <a
            data-testid="youtube-channel-link"
            href="https://youtube.com/@beautesolutionssherrycorvino?si=XZFAKwZm1F6NJBd9"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full border border-clay/40 bg-card/70 px-6 py-3 text-sm font-semibold text-clay transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 hover:bg-clay hover:text-cream"
          >
            <Youtube size={18} />
            Watch more on YouTube — @beautesolutionssherrycorvino
          </a>
        </Reveal>

        <Reveal delay={0.15} className="mt-12 text-center">
          <a
            data-testid="before-after-cta-button"
            href={BRAND.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 rounded-full bg-espresso px-8 py-4 text-sm font-semibold text-cream transition-[background-color,transform] duration-300 hover:bg-clay hover:-translate-y-0.5"
          >
            {BEFORE_AFTER.cta}
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
